import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, AlertTriangle, AlertCircle, Info } from 'lucide-react';

interface Phase2Difference {
  topic: string;
  severity: 'CRITICAL' | 'MEDIUM' | 'LOW';
  commercial_impact: string;
  differences: string[];
  recommendation: string;
  section_reference: string;
}

interface Phase2Summary {
  total_issues: number;
  critical_issues: number;
  medium_issues: number;
  low_issues: number;
}

interface DORAExecutiveSummaryProps {
  phase2Data?: {
    differences: Phase2Difference[];
    summary: Phase2Summary;
    seller_name?: string;
    buyer_name?: string;
  };
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'CRITICAL':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'MEDIUM':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'LOW':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'CRITICAL':
      return <AlertTriangle className="h-4 w-4" />;
    case 'MEDIUM':
      return <AlertCircle className="h-4 w-4" />;
    case 'LOW':
      return <Info className="h-4 w-4" />;
    default:
      return null;
  }
};

export function DORAExecutiveSummary({ phase2Data }: DORAExecutiveSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!phase2Data || phase2Data.differences.length === 0) {
    return null;
  }

  const { summary, differences, seller_name = 'Seller', buyer_name = 'Buyer' } = phase2Data;

  return (
    <Card className="border-2 border-orange-200 bg-orange-50/50 mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            Compliance Analysis Executive Summary
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="ml-2"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Collapse
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Expand Details
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Summary Statistics */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="text-center p-3 bg-white rounded-lg border">
            <div className="text-2xl font-bold text-gray-900">{summary.total_issues}</div>
            <div className="text-sm text-gray-600">Total Issues</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
            <div className="text-2xl font-bold text-red-700">{summary.critical_issues}</div>
            <div className="text-sm text-red-600">Critical</div>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
            <div className="text-2xl font-bold text-orange-700">{summary.medium_issues}</div>
            <div className="text-sm text-orange-600">Medium</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">{summary.low_issues}</div>
            <div className="text-sm text-blue-600">Low</div>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="mt-4 space-y-4 border-t pt-4">
            <h4 className="font-semibold text-sm text-gray-700">All Issues by Severity:</h4>
            
            {differences.map((diff, idx) => (
              <div key={idx} className={`p-4 rounded-lg border-2 ${getSeverityColor(diff.severity)}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getSeverityIcon(diff.severity)}
                    <h5 className="font-semibold">
                      {diff.topic.replace(/_/g, ' ').toUpperCase()}
                    </h5>
                  </div>
                  <Badge variant="outline" className={getSeverityColor(diff.severity)}>
                    {diff.severity}
                  </Badge>
                </div>
                
                <div className="text-sm space-y-2">
                  <div>
                    <span className="font-medium">Key Differences ({buyer_name} Requirements vs. {seller_name} Standard Terms):</span>
                    <ul className="list-disc list-inside ml-2 mt-1">
                      {diff.differences.map((d, i) => (
                        <li key={i} className="text-gray-700">{d}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <span className="font-medium">Cost Impact:</span>
                    <p className="ml-2 mt-1 font-semibold">
                      {diff.commercial_impact}
                      <span className="ml-2 font-normal text-muted-foreground text-sm">
                        {diff.commercial_impact === '$$$$' && '(Critical - major cost burden)'}
                        {diff.commercial_impact === '$$$' && '(Medium-High - requires pricing adjustment)'}
                        {diff.commercial_impact === '$$' && '(Low-Medium - noticeable but manageable)'}
                        {diff.commercial_impact === '$' && '(Low - minor costs)'}
                      </span>
                    </p>
                  </div>
                  
                  <div className="bg-white/50 p-2 rounded mt-2">
                    <span className="font-medium">Section:</span> {diff.section_reference}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
